import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { useFriendAction, useFriends, useSendFriendRequest } from '../api/hooks'
import type { FriendListItem, FriendUser } from '../api/types'
import { ScreenShell } from '../components/ScreenShell'
import { formatDateBare } from '../datetime'
import { MeepleAvatar, Pill, ScorePad, ScorePadRow, type Tab, Tabs, TopBar } from '../primitives'

type TabId = 'friends' | 'incoming' | 'outgoing' | 'blocked'

const TAB_DEFS: ReadonlyArray<Tab<TabId>> = [
  { id: 'friends', label: 'Friends' },
  { id: 'incoming', label: 'Incoming' },
  { id: 'outgoing', label: 'Outgoing' },
  { id: 'blocked', label: 'Blocked' },
]

const formatSince = (iso: string): string => formatDateBare(iso)

export const FriendsScreen = () => {
  const friends = useFriends()
  const send = useSendFriendRequest()
  const actions = useFriendAction()
  const [tab, setTab] = useState<TabId>('friends')
  const [handle, setHandle] = useState('')
  const [error, setError] = useState<string | null>(null)

  const onSend = async () => {
    setError(null)
    const trimmed = handle.trim()
    if (!trimmed) return
    try {
      const result = await send.mutateAsync(trimmed)
      setHandle('')
      if (result.resultStatus === 'accepted') {
        // Reciprocal pending was auto-accepted, jump to Friends tab.
        setTab('friends')
      } else {
        setTab('outgoing')
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.friendlyMessage : 'Could not send request')
    }
  }

  const tabsWithCounts: ReadonlyArray<Tab<TabId>> = TAB_DEFS.map((t) => ({
    ...t,
    count:
      t.id === 'friends'
        ? friends.data?.accepted.length
        : t.id === 'incoming'
          ? friends.data?.incoming.length
          : t.id === 'outgoing'
            ? friends.data?.outgoing.length
            : friends.data?.blocked.length,
  }))

  return (
    <ScreenShell active="friends">
      <TopBar title="Friends" />

      <div style={{ padding: '14px 16px' }}>
        <div
          className="felt-card"
          style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <div className="kicker">add a friend</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="handle (e.g. cora)"
              value={handle}
              onChange={(e) => setHandle(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSend()
              }}
              style={{ flex: 1 }}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <button
              type="button"
              className="btn-stamp"
              onClick={onSend}
              disabled={send.isPending || !handle.trim()}
              style={{ padding: '10px 14px' }}
            >
              {send.isPending ? 'Sending…' : 'Send'}
            </button>
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--terra-ink)' }}>{error}</div>}
        </div>
      </div>

      <Tabs tabs={tabsWithCounts} active={tab} onChange={setTab} />

      <div style={{ padding: '14px 16px' }}>
        {friends.isLoading && <div style={{ color: 'var(--ink-3)' }}>Loading…</div>}
        {friends.data && (
          <>
            {tab === 'friends' && (
              <FriendList
                items={friends.data.accepted}
                emptyMsg="No friends yet. Send a request above."
                renderActions={(u) => (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => actions.unfriend.mutate(u.id)}
                      disabled={actions.unfriend.isPending}
                      style={{ padding: '4px 10px' }}
                    >
                      Unfriend
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => actions.block.mutate(u.id)}
                      disabled={actions.block.isPending}
                      style={{
                        padding: '4px 10px',
                        color: 'var(--terra-ink)',
                        borderColor: 'var(--terra)',
                      }}
                    >
                      Block
                    </button>
                  </div>
                )}
              />
            )}
            {tab === 'incoming' && (
              <FriendList
                items={friends.data.incoming}
                emptyMsg="No pending requests."
                subLabel={(it) => `requested ${formatSince(it.since)}`}
                renderActions={(u) => (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      className="btn-stamp"
                      onClick={() => actions.accept.mutate(u.id)}
                      disabled={actions.accept.isPending}
                      style={{ padding: '4px 10px' }}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => actions.decline.mutate(u.id)}
                      disabled={actions.decline.isPending}
                      style={{ padding: '4px 10px' }}
                    >
                      Decline
                    </button>
                  </div>
                )}
              />
            )}
            {tab === 'outgoing' && (
              <FriendList
                items={friends.data.outgoing}
                emptyMsg="No outgoing requests."
                subLabel={(it) => `sent ${formatSince(it.since)}`}
                renderActions={(u) => (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => actions.decline.mutate(u.id)}
                    disabled={actions.decline.isPending}
                    style={{ padding: '4px 10px' }}
                  >
                    Cancel
                  </button>
                )}
                rightChip={<Pill color="slate">pending</Pill>}
              />
            )}
            {tab === 'blocked' && (
              <FriendList
                items={friends.data.blocked}
                emptyMsg="Nobody blocked."
                subLabel={(it) => `blocked ${formatSince(it.since)}`}
                renderActions={(u) => (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => actions.unblock.mutate(u.id)}
                    disabled={actions.unblock.isPending}
                    style={{ padding: '4px 10px' }}
                  >
                    Unblock
                  </button>
                )}
              />
            )}
          </>
        )}
      </div>
    </ScreenShell>
  )
}

const FriendList = ({
  items,
  emptyMsg,
  subLabel,
  rightChip,
  renderActions,
}: {
  items: FriendListItem[]
  emptyMsg: string
  subLabel?: (item: FriendListItem) => string
  rightChip?: React.ReactNode
  renderActions: (u: FriendUser) => React.ReactNode
}) => {
  if (items.length === 0) {
    return (
      <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
        <div style={{ fontSize: 13 }}>{emptyMsg}</div>
      </div>
    )
  }
  return (
    <ScorePad>
      {items.map((it) => (
        <ScorePadRow key={it.user.id}>
          <MeepleAvatar
            name={it.user.displayName}
            hue={it.user.avatarHue}
            shape={it.user.avatarShape}
            size={36}
          />
          <Link
            to="/u/$handle"
            params={{ handle: it.user.handle }}
            style={{
              flex: 1,
              minWidth: 0,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>
              {it.user.displayName}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              @{it.user.handle}
              {subLabel && ` · ${subLabel(it)}`}
            </div>
          </Link>
          {rightChip}
          {renderActions(it.user)}
        </ScorePadRow>
      ))}
    </ScorePad>
  )
}
